import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReportTracker } from '../hooks/useReportTracker';
import type { ReportNotification as ReportNotificationType } from '../context/ReportTrackerContextBase';

function formatReportLabel(reportType: 'astrology' | 'lunar'): string {
  return reportType === 'astrology' ? 'Astrology Report' : 'Lunar Report';
}

export const ReportNotifications: React.FC = () => {
  const { notifications, acknowledgeNotification, markNotificationAnnounced } = useReportTracker();
  const navigate = useNavigate();
  const permissionRequestedRef = useRef(false);
  const emittedReadyRef = useRef<Set<string>>(new Set());

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [notifications]);

  const openDestination = useCallback((notification: ReportNotificationType) => {
    const hasToken = notification.token && notification.token.length > 0;
    const tokenQuery = hasToken ? `?token=${encodeURIComponent(notification.token!)}` : '';

    if (notification.status === 'completed') {
      const url = `/report-library${tokenQuery}`;
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener');
      } else {
        navigate(url);
      }
    } else {
      const base = `/report-status`;
      const params = new URLSearchParams();
      if (hasToken) {
        params.set('token', notification.token!);
      }
      params.set('profileId', notification.profileId);
      params.set('sessionId', notification.sessionId);
      navigate(`${base}?${params.toString()}`);
    }
  }, [navigate]);

  useEffect(() => {
    sortedNotifications.forEach(notification => {
      if (notification.announced) {
        return;
      }

      if (typeof window === 'undefined' || typeof Notification === 'undefined') {
        markNotificationAnnounced(notification.id);
        return;
      }

          const triggerNotification = () => {
            try {
              const title = notification.status === 'completed'
                ? 'Your reports are ready'
                : `${formatReportLabel(notification.reportType)} failed`;
              const body = notification.status === 'completed'
            ? 'Tap to view your reports in your library.'
            : notification.errorMessage || 'Tap to review and retry.';

          const systemNotification = new Notification(title, {
            body,
            tag: notification.id
          });

          systemNotification.onclick = () => {
            window.focus();
            openDestination(notification);
          };
        } catch (error) {
          console.warn('[ReportNotifications] Failed to show system notification:', error);
        } finally {
          markNotificationAnnounced(notification.id);
        }
      };

      if (Notification.permission === 'granted') {
        triggerNotification();
          } else if (Notification.permission === 'default' && !permissionRequestedRef.current) {
            permissionRequestedRef.current = true;
            Notification.requestPermission().finally(() => {
              if (Notification.permission === 'granted') {
                triggerNotification();
          } else {
            markNotificationAnnounced(notification.id);
          }
        });
      } else {
        markNotificationAnnounced(notification.id);
      }
    });
  }, [sortedNotifications, markNotificationAnnounced, openDestination]);

  const dedupedNotifications = useMemo(() => {
    const seen = new Set<string>();
    const result: ReportNotificationType[] = [];
    sortedNotifications.forEach((note) => {
      const key = note.status === 'completed' && note.sessionId
        ? `${note.sessionId}:completed`
        : note.id;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      result.push(note);
    });
    return result;
  }, [sortedNotifications]);

  // Broadcast a "reports ready" event for other UI (e.g., Luna) to react
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    dedupedNotifications.forEach(note => {
      if (note.status !== 'completed') {
        return;
      }
      if (emittedReadyRef.current.has(note.id)) {
        return;
      }

      emittedReadyRef.current.add(note.id);

      const hasToken = note.token && note.token.length > 0;
      const tokenQuery = hasToken ? `?token=${encodeURIComponent(note.token!)}` : '';
      const libraryUrl = `/report-library${tokenQuery}`;

      window.dispatchEvent(
        new CustomEvent('reports:ready', {
          detail: {
            id: note.id,
            sessionId: note.sessionId,
            profileId: note.profileId,
            reportType: note.reportType,
            url: libraryUrl
          }
        })
      );
    });
  }, [dedupedNotifications]);

  if (dedupedNotifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 z-[9999] flex flex-col gap-3 max-w-sm">
      {dedupedNotifications.map(notification => {
        const isCompleted = notification.status === 'completed';
        const title = isCompleted
          ? 'Your reports are ready'
          : `${formatReportLabel(notification.reportType)} failed`;
        const description = isCompleted
          ? 'Tap to view your reports in your library.'
          : notification.errorMessage || 'We hit a snag. Tap to review and try again.';

        return (
          <div
            key={notification.id}
            data-testid="report-notification-toast"
            className="bg-white shadow-xl rounded-xl border border-black/5 p-4 flex flex-col gap-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">{title}</p>
                <p className="text-xs text-gray-600 mt-1">{new Date(notification.createdAt).toLocaleTimeString()}</p>
              </div>
              <button
                type="button"
                onClick={() => acknowledgeNotification(notification.id)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-700">{description}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  openDestination(notification);
                  acknowledgeNotification(notification.id);
                }}
                className="flex-1 bg-gradient-to-r from-forest-deep to-antique-gold text-manuscript-cream text-sm font-semibold py-2 px-3 rounded-lg hover:scale-[1.01] transition-transform"
              >
                {isCompleted ? 'Open report library' : 'Resolve issue'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
